import { apiClient as api } from "./httpClient";
import { ScheduleSlot } from "./schedulesService";

/** Bloqueio de agenda (RN-118n): marcação sem paciente, não cobrável; datas no relógio da
 *  entidade. followingCount = bloqueios da série depois deste ("este e os seguintes"). */
export interface ScheduleBlockItem {
  id: string;
  professionalRegistrationId: string;
  dateTimeInitial: string;
  dateTimeFinal: string;
  title: string;
  notes: string | null;
  seriesId: string;
  followingCount: number;
  canEdit: boolean;
  /** já terminou: só leitura (trava do passado) */
  isPast: boolean;
}

export interface ScheduleBlockOccurrence {
  id: string;
  dateTimeInitial: string;
  dateTimeFinal: string;
  isPast: boolean;
}

/** Série de bloqueios da página "Bloqueios de agenda". startTime/endTime nulos = horários
 *  variados; nextId = primeira ocorrência por vir (alvo do "daqui em diante"). */
export interface ScheduleBlockSeriesItem {
  seriesId: string;
  professionalRegistrationId: string;
  professionalName: string;
  title: string;
  notes: string | null;
  weekdays: number[];
  startTime: string | null;
  endTime: string | null;
  firstDate: string;
  lastDate: string;
  total: number;
  futureCount: number;
  nextId: string | null;
  canEdit: boolean;
  occurrences: ScheduleBlockOccurrence[];
}

export interface CreateScheduleBlockPayload {
  professionalRegistrationId: string;
  title: string;
  notes: string | null;
  slots: ScheduleSlot[];
}

/** Data e horários valem só para este; applyToFollowing leva título, observações e horários
 *  (não as datas) aos seguintes da série. */
export interface UpdateScheduleBlockPayload {
  date: string;
  startTime: string;
  endTime: string;
  title: string;
  notes: string | null;
  applyToFollowing: boolean;
}

export const scheduleBlocksService = {
  /** from/to: datas locais (yyyy-MM-dd), inclusivas; professionals: CSV de ids de UEPR */
  getBlocks: async (from: string, to: string, professionals: string): Promise<ScheduleBlockItem[]> => {
    const response = await api.get("/schedule-blocks", { params: { from, to, professionals } });
    return response.data.items;
  },

  /** Mesmo que getBlocks, em fatias de 60 dias: a API aceita janelas de até 62 e as telas de
   *  impressão e a Agenda do dia deixam escolher período livre. */
  getBlocksInRange: async (from: string, to: string, professionals: string): Promise<ScheduleBlockItem[]> => {
    if (!from || !to || !professionals) return [];
    const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    const windows: [string, string][] = [];
    let cursor = new Date(`${from}T00:00:00`);
    const last = new Date(`${to}T00:00:00`);
    while (cursor <= last && windows.length < 24) {
      const end = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate() + 59);
      windows.push([iso(cursor), iso(end < last ? end : last)]);
      cursor = new Date(end.getFullYear(), end.getMonth(), end.getDate() + 1);
    }
    const parts = await Promise.all(windows.map(([f, t]) =>
      api.get("/schedule-blocks", { params: { from: f, to: t, professionals } }).then(r => r.data.items as ScheduleBlockItem[])));
    // Bloqueio que atravessa a borda de uma fatia vem nas duas
    const byId = new Map<string, ScheduleBlockItem>();
    parts.flat().forEach(b => byId.set(b.id, b));
    return Array.from(byId.values()).sort((a, b) => a.dateTimeInitial.localeCompare(b.dateTimeInitial));
  },

  getSeries: async (professionals: string, includePast: boolean): Promise<ScheduleBlockSeriesItem[]> => {
    const response = await api.get("/schedule-blocks/series", { params: { professionals, includePast } });
    return response.data.items;
  },

  create: async (payload: CreateScheduleBlockPayload): Promise<{ seriesId: string; ids: string[] }> => {
    const response = await api.post("/schedule-blocks", payload);
    return response.data;
  },

  update: async (id: string, payload: UpdateScheduleBlockPayload): Promise<number> => {
    const response = await api.put(`/schedule-blocks/${id}`, payload);
    return response.data.updated;
  },

  remove: async (id: string, includeFollowing: boolean): Promise<number> => {
    const response = await api.delete(`/schedule-blocks/${id}`, { params: { includeFollowing } });
    return response.data.deleted;
  },
};

/** Erros de negócio do bloqueio em PT-BR (code do Result.Error do backend). */
export const mapScheduleBlockError = (error: any): string => {
  const code: string = error?.response?.data?.code ?? '';
  const messages: Record<string, string> = {
    'ScheduleBlocks.TitleRequired': 'Informe o tipo de bloqueio.',
    'ScheduleBlocks.OwnScopeDenied': 'Seu perfil só permite bloquear a própria agenda.',
    'ScheduleBlocks.ProfessionalNotFound': 'Profissional não encontrado nesta clínica.',
    'ScheduleBlocks.NotFound': 'Bloqueio não encontrado (pode ter sido excluído).',
    'ScheduleBlocks.Overlap': error?.response?.data?.description || 'Já existe bloqueio deste profissional nesse horário.',
    'ScheduleBlocks.PastLocked': 'Bloqueio que já passou não pode ser alterado nem excluído.',
    'ScheduleBlocks.PastDate': 'O bloqueio não pode ser criado nem movido para uma data que já passou.',
    'ScheduleBlocks.InvalidSlots': 'Há horários em conflito: confira os bloqueios que se sobrepõem ou terminam antes de começar.',
  };
  return messages[code] || 'Não foi possível concluir a operação. Tente novamente.';
};
